import { toast } from 'sonner';
import { Mic, Square } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useBlocker } from '@tanstack/react-router';

import {
  EditableUltrasoundReport,
  UltrasoundSignatureArea,
  type ReportForm,
  type ReportPayload,
} from './EditableUltrasoundReport';
import { CineViewer } from './CineViewer';
import { clientApi } from '@/config/axios';
import { ImageViewer } from './ImageViewer';
import { AttachmentViewer } from './AttachmentViewer';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { TrendFigures } from './TrendFigures';
import { uploadToS3 } from '@/features/uploads';
import { OrthancViewer } from './OrthancViewer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { usePickerTemplates } from '@/features/templates';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { apiError, type Detail, useImagingApi, useImagingCapabilities, useImagingQuery } from './api';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Refresh source fields after evidence saves without overwriting physician edits.
export function mergeReportEvidence(
  current: ReportPayload | null,
  previous: ReportPayload | null,
  form: ReportForm | null,
): ReportPayload | null {
  if (!form) return null;
  const next = form.report;
  if (!current || current.templateId !== next.templateId || current.fetusId !== next.fetusId) return next;
  if (!previous || previous.templateId !== next.templateId || previous.fetusId !== next.fetusId) {
    // Older drafts predate the full anatomy field. Add reviewed source anatomy
    // when absent; retain existing text (including an intentional blank).
    if (!('fetalAnatomy' in current.fields) && next.fields.fetalAnatomy) {
      return { ...current, fields: { ...current.fields, fetalAnatomy: next.fields.fetalAnatomy } };
    }
    return current;
  }
  const fields = { ...next.fields };
  for (const key of new Set([...Object.keys(previous.fields), ...Object.keys(current.fields)])) {
    if (form.field_definitions[key]?.readOnly) continue;
    if (JSON.stringify(current.fields[key]) !== JSON.stringify(previous.fields[key])) {
      if (key in current.fields) fields[key] = current.fields[key];
      else delete fields[key];
    }
  }
  return { ...next, fields };
}

interface SigningPreview {
  attestation: string;
  expected_hash: string;
  image_url: string;
  signer_credentials: string;
  signer_name: string;
  snapshot: {
    signature_text?: string;
    content: string;
    patient_name: string;
    title: string;
    imaging: { figures: import('./api').Series[] };
  };
}
export function StudyReview({ studyId }: { studyId: string }) {
  const { org, userId } = useImagingApi();
  const cap = useImagingCapabilities();
  const allowed = cap.data?.permissions.some((p) => ['prepare_imaging', 'read_imaging'].includes(p));
  const query = useImagingQuery<Detail>(`/studies/${studyId}`, !!allowed);
  if (!cap.isLoading && !allowed) return <p>Clinical imaging access is required.</p>;
  if (query.isError)
    return (
      <Alert>
        <AlertDescription>{apiError(query.error)}</AlertDescription>
      </Alert>
    );
  if (!query.data) return <p>Loading study…</p>;
  return (
    <Editor
      key={`${org}:${userId}:${studyId}`}
      initial={query.data}
      transcription={!!cap.data?.transcription_enabled}
      clinical={!!cap.data?.permissions.includes('read_imaging')}
      intake={!!cap.data?.permissions.includes('intake_imaging')}
      prepare={!!cap.data?.permissions.includes('prepare_imaging')}
    />
  );
}
function Editor({
  intake,
  initial,
  prepare,
  clinical,
  transcription,
}: {
  initial: Detail;
  prepare: boolean;
  clinical: boolean;
  transcription: boolean;
  intake: boolean;
}) {
  const [study, setStudy] = useState(initial);
  const { org, base } = useImagingApi();
  const qc = useQueryClient();
  const templates = usePickerTemplates({ active_only: true, category: 'document' });
  const [template, setTemplate] = useState(initial.draft.template_id || '');
  const [reportPayload, setReportPayload] = useState<ReportPayload | null>(initial.draft.report_payload || null);
  const [reportForm, setReportForm] = useState<ReportForm | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [fetus, setFetus] = useState(
    initial.draft.report_payload?.fetusId || ((initial.context?.dating?.fetus_count || 1) === 1 ? '1' : ''),
  );
  const reportSection = useRef<HTMLElement>(null);
  const reportBaseline = useRef<ReportPayload | null>(null);
  const evidenceVersion = JSON.stringify(study.evidence);
  useEffect(() => {
    if (!template || !study.pregnancy_id || !fetus) return;
    let active = true;
    setFormLoading(true);
    setFormError('');
    clientApi
      .get<ReportForm | null>(`${base}/studies/${study.id}/report-form`, {
        params: { template_id: template, fetus_id: fetus },
      })
      .then(({ data }) => {
        if (!active) return;
        const previous = reportBaseline.current;
        reportBaseline.current = data?.report || null;
        setReportForm(data);
        setReportPayload((current) => mergeReportEvidence(current, previous, data));
      })
      .catch((error) => {
        if (active) {
          setFormError(apiError(error));
          setReportForm(null);
        }
      })
      .finally(() => {
        if (active) setFormLoading(false);
      });
    return () => {
      active = false;
    };
  }, [template, fetus, study.id, study.pregnancy_id, study.signed_document_id, base, evidenceVersion]);
  const [comments, setComments] = useState(initial.evidence.comments || '');
  const studyDate = initial.metadata.StudyDate || '';
  const [acquired, setAcquired] = useState(
    initial.evidence.acquired_date ||
      (/^\d{8}$/.test(studyDate) ? `${studyDate.slice(0, 4)}-${studyDate.slice(4, 6)}-${studyDate.slice(6, 8)}` : ''),
  );
  const [impression, setImpression] = useState(initial.draft.impression || '');
  const [recommendations, setRecommendations] = useState(initial.draft.recommendations || '');
  const [figures, setFigures] = useState(initial.draft.figure_keys || []);
  const [evidenceDirty, setEvidenceDirty] = useState(false);
  const [evidenceConfirmed, setEvidenceConfirmed] = useState(false);
  const [draftDirty, setDraftDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audio, setAudio] = useState<Blob | null>(null);
  const [transcriptReview, setTranscriptReview] = useState(false);
  const [preview, setPreview] = useState<SigningPreview | null>(null);
  const [attested, setAttested] = useState(false);
  const requestKey = useRef('');
  const mounted = useRef(true);
  const recorder = useAudioRecorder({
    onInterrupt: () => toast.error('Microphone interrupted. Stop to recover available audio.'),
    onError: () => toast.error('Recording interrupted. Stop and retain the audio before retrying.'),
  });
  const locked = !!study.signed_document_id;
  const editable = prepare && !locked;
  const extraction = study.extraction || {};
  const extracting = ['downloading', 'processing', 'queued'].includes(extraction.status || '');
  const extractionReady = ['complete', 'partial'].includes(extraction.status || '');
  const savedExtractionCurrent =
    !extractionReady || (!!extraction.job_id && study.evidence.reviewed_extraction?.job_id === extraction.job_id);
  const needsEvidenceSave = evidenceDirty || !study.evidence.reviewed || !savedExtractionCurrent;
  const draftBlockers = [
    !editable && (locked ? 'This report is signed and locked.' : 'Report preparation permission is required.'),
    !study.pregnancy_id && 'Match the study to a patient and pregnancy.',
    !fetus && 'Select the fetus for this report.',
    formLoading && 'Loading the selected report layout.',
    !!formError && formError,
    !acquired && 'Enter the acquisition date below.',
    !template && 'Choose an impression template below.',
    extracting && 'Wait for study extraction to finish.',
    recording && 'Stop the recording.',
    transcriptReview && 'Review the transcript and select Transcript reviewed.',
    !!audio && !transcriptReview && 'Transcribe or discard the pending recording.',
    needsEvidenceSave && !evidenceConfirmed && 'Review the Biometry Summary and report details, then confirm below.',
  ].filter((message): message is string => !!message);
  useEffect(() => {
    setEvidenceConfirmed(false);
  }, [comments, acquired, extraction.job_id, extraction.status, study.revision]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      recorder.cancel();
    };
    // `recorder.cancel`, not `recorder`. The rule wants the whole object, but it
    // is rebuilt each render, so this cleanup would re-run constantly and cancel
    // a recording mid-dictation. The cancel function itself is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder.cancel]);
  useBlocker({
    enableBeforeUnload: () => !!(evidenceDirty || draftDirty || recording || audio),
    shouldBlockFn: () =>
      !!(evidenceDirty || draftDirty || recording || audio) &&
      !window.confirm('Leave this study? Unsaved changes and recordings will be lost.'),
  });
  useEffect(() => {
    const guard = (e: BeforeUnloadEvent) => {
      if (evidenceDirty || draftDirty || recording || audio) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [evidenceDirty, draftDirty, recording, audio]);
  async function refresh() {
    const result = (await clientApi.get<Detail>(`${base}/studies/${study.id}`)).data;
    if (mounted.current) setStudy(result);
    await qc.invalidateQueries({ queryKey: ['imaging', org] });
    return result;
  }
  useEffect(() => {
    if (!extracting) return;
    const timer = window.setInterval(() => {
      void refresh().catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
    // Study identity is fixed for the mounted editor; only job status controls polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extracting, study.id]);
  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
  async function record() {
    if (recording) {
      const result = await recorder.stop();
      setRecording(false);
      setAudio(result.blob);
    } else {
      await recorder.start();
      setRecording(true);
    }
  }
  const dictationReceipt = useRef<{ blob: Blob; object_name: string; sha256: string } | null>(null);
  const [dictating, setDictating] = useState(false);
  async function transcribe() {
    if (!audio || !org) return;
    if (audio.size > 24 * 1024 * 1024) throw new Error('Recording must be at most 24 MiB.');
    setDictating(true);
    try {
      if (dictationReceipt.current?.blob !== audio) {
        const mime = audio.type.split(';')[0].toLowerCase();
        const extension = (
          { 'audio/mp4': 'mp4', 'audio/wav': 'wav', 'audio/ogg': 'ogg', 'audio/webm': 'webm' } as Record<string, string>
        )[mime];
        if (!extension) throw new Error('Unsupported recording format. Download the recording before retrying.');
        const hash = await crypto.subtle.digest('SHA-256', await audio.arrayBuffer());
        const digest = Array.from(new Uint8Array(hash), (v) => v.toString(16).padStart(2, '0')).join('');
        const uploaded = await uploadToS3(
          org,
          new File([audio], `comments.${extension}`, { type: mime }),
          'imaging-dictation',
        );
        dictationReceipt.current = { blob: audio, sha256: digest, object_name: uploaded.objectName };
      }
      const { sha256, object_name } = dictationReceipt.current!;
      const body = { sha256, object_name };
      await clientApi.post(`${base}/studies/${study.id}/dictations`, body);
      const deadline = Date.now() + 45 * 60 * 1000;
      while (mounted.current && Date.now() < deadline) {
        const { data } = await clientApi.post(`${base}/studies/${study.id}/dictations/status`, body);
        if (!mounted.current) return;
        if (data.status === 'failed') throw new Error(data.error);
        if (data.status === 'ready') {
          setComments((v) => (v ? `${v}\n${data.text}` : data.text));
          setEvidenceDirty(true);
          setTranscriptReview(true);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
      if (mounted.current) throw new Error('Transcription is still pending. Keep the recording and retry.');
    } catch {
      if (mounted.current)
        toast.error('Transcription did not finish. Your recording is retained; download it or retry.');
    } finally {
      if (mounted.current) setDictating(false);
    }
  }

  async function saveEvidence(): Promise<Detail> {
    await clientApi.put(`${base}/studies/${study.id}/evidence`, {
      comments,
      reviewed: true,
      acquired_date: acquired,
      revision: study.revision,
      observations: study.evidence.observations || [],
      extraction_job_id: extractionReady ? extraction.job_id : null,
    });
    const saved = await refresh();
    setEvidenceDirty(false);
    setEvidenceConfirmed(false);
    setDraftDirty(true);
    return saved;
  }
  async function generate() {
    if (draftBlockers.length) return;
    if (
      reportPayload &&
      draftDirty &&
      impression.trim() &&
      !window.confirm('Generating again replaces your unsaved report fields and impression. Continue?')
    )
      return;
    setGenerationError('');
    try {
      // Use the revision returned after saving, not the render's stale state.
      const current = needsEvidenceSave ? await saveEvidence() : study;
      const result = await clientApi.post(`${base}/studies/${study.id}/generate`, {
        template_id: template,
        revision: current.revision,
        fetus_id: fetus,
      });
      if (result.data.report) {
        setReportPayload(result.data.report);
        setReportForm(result.data);
      }
      setImpression(result.data.impression);
      setDraftDirty(true);
      toast.success('Draft report populated. Review the fields and enter recommendations below.');
      reportSection.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      setGenerationError(apiError(error));
      throw error;
    }
  }
  async function extractStudy() {
    await clientApi.post(`${base}/studies/${study.id}/extract-study`, { revision: study.revision });
    await refresh();
    toast.info('Study extraction is running in the background. You can return later.');
  }
  async function saveDraft() {
    const payload: ReportPayload | null = reportPayload
      ? { ...reportPayload, fields: { ...reportPayload.fields, impression } }
      : null;
    if (payload) {
      if (recommendations.trim()) payload.fields.recommendations = recommendations;
      else delete payload.fields.recommendations;
    }
    await clientApi.put(`${base}/studies/${study.id}/draft`, {
      figures,
      impression,
      recommendations,
      template_id: template,
      revision: study.revision,
      draft_revision: study.draft_revision,
      report_payload: payload,
    });
    await refresh();
    setDraftDirty(false);
    toast.success('Draft and figures saved for provider review.');
  }
  async function reviewSign() {
    const result = await clientApi.get<SigningPreview>(`${base}/studies/${study.id}/signing-preview`);
    setPreview(result.data);
    setAttested(false);
    requestKey.current = crypto.randomUUID();
  }
  async function sign() {
    if (!preview || !attested) return;
    await clientApi.post(`${base}/studies/${study.id}/sign`, {
      attestation: preview.attestation,
      idempotency_key: requestKey.current,
      expected_hash: preview.expected_hash,
    });
    await refresh();
    setPreview(null);
    toast.success('Report signed and published to the patient chart.');
  }
  return (
    <div className="flex flex-col gap-5 p-6">
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <h1>{study.metadata.PatientName || 'Ultrasound study'}</h1>
          <p>
            {study.metadata.StudyDescription} · {study.metadata.PatientID}
          </p>
          <p>
            Pregnancy {study.pregnancy_id} · EDD {study.context?.dating?.edd || 'not established'}
          </p>
        </div>
        <Link to="/imaging">Reading list</Link>
      </div>
      {!study.pregnancy_id && (
        <Alert>
          <AlertDescription>
            Match this study to its patient and pregnancy in <Link to="/imaging">Imaging intake</Link> before saving
            measurements or creating an impression. Confirm the DICOM identifiers against the chart first.
          </AlertDescription>
        </Alert>
      )}
      {study.context?.dating_warning && (
        <Alert>
          <AlertDescription>
            {study.context.dating_warning} Verify the pregnancy match before using the flowsheet or finalizing a report.
          </AlertDescription>
        </Alert>
      )}
      {locked && (
        <Alert>
          <AlertDescription>
            This report is signed and locked. Open the patient chart for the signed PDF and amendment workflow.
          </AlertDescription>
        </Alert>
      )}
      <div className="grid gap-5 xl:grid-cols-2">
        <section className="flex h-[780px] min-w-0 flex-col rounded-lg border">
          <header className="p-4">
            <h2>Study Evidence</h2>
          </header>
          <div aria-label="Study evidence" className="min-h-0 flex-1 overflow-y-auto p-4">
            <OrthancViewer studyId={study.id} />
            {!!study.attachments?.length && (
              <p>Run Extract full study below to prepare JPG/PDF pages for OHIF and measurement review.</p>
            )}
            <ImageViewer studyId={study.id} objects={study.objects} />
            <AttachmentViewer studyId={study.id} attachments={study.attachments || []} />
            <p>
              {study.metadata.ManufacturerModelName} · revision {study.revision}
            </p>
            <details>
              <summary>Imaging objects ({study.objects.length})</summary>
              {study.objects.map((o) => (
                <div key={o.id}>
                  <Button
                    variant="link"
                    onClick={() =>
                      void run(async () => {
                        const r = await clientApi.get(`${base}/studies/${study.id}/objects/${o.id}/download`);
                        window.open(r.data.url, '_blank', 'noopener,noreferrer');
                      })
                    }>
                    Download DICOM{' '}
                    {o.image_info?.source_filename
                      ? `${o.image_info.source_filename} · page ${o.image_info.source_page}`
                      : o.sop_uid}
                  </Button>
                </div>
              ))}
            </details>
            <CineViewer canRetry={intake} studyId={study.id} />
            {!!study.evidence.observations?.length && (
              <p>
                {study.evidence.observations.length} previously saved flowsheet measurements remain on this study. The
                Biometry Summary below is used for the draft impression.
              </p>
            )}
            <div className="my-4 rounded border p-3">
              <h3>Biometry Summary and report details</h3>
              <p>
                One background job screens the full study, reads DICOM SR, JPG images, every PDF page, and sampled cine
                frames. JPG/PDF pages are also prepared for OHIF. Values and flags require clinical verification before
                entering the flowsheet or AI draft.
              </p>
              {editable && (
                <Button variant="outline" disabled={busy || extracting} onClick={() => void run(extractStudy)}>
                  {extraction.status === 'complete' || extraction.status === 'partial'
                    ? 'Run study extraction again'
                    : 'Extract full study'}
                </Button>
              )}
              {extracting && (
                <p role="status">
                  {extraction.status}: {extraction.completed || 0} of {extraction.total || study.objects.length} objects
                  processed. You may leave this page; the job will continue.
                </p>
              )}
              {extraction.status === 'failed' && (
                <p role="alert">{extraction.error || 'Extraction failed.'} Retry the study job.</p>
              )}
              {extraction.status === 'partial' && (
                <p role="alert">
                  Extraction was incomplete ({extraction.error_count || 0} object errors or changed source files).
                  Review available values and rerun after checking the source study.
                </p>
              )}
              {['complete', 'partial'].includes(extraction.status || '') && (
                <p>
                  {extraction.candidate_count || 0} source readings found by pipeline {extraction.pipeline_version}. No
                  values were saved to the flowsheet automatically.
                </p>
              )}
              {extractionReady && (
                <p>
                  Percentile dating:{' '}
                  {extraction.source_context?.percentile_ga_days != null
                    ? `${Math.floor(extraction.source_context.percentile_ga_days / 7)}w${Math.round(extraction.source_context.percentile_ga_days % 7)}d · ${extraction.source_context.percentile_ga_basis || 'source study'}`
                    : 'No independent study dating available; scanner percentiles, if present, are labeled separately.'}
                </p>
              )}
              {extraction.table?.length ? (
                <details open className="my-3">
                  <summary>Biometry summary ({extraction.table.length} parameters; verify against source)</summary>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr>
                          <th>Structure</th>
                          <th>Fetus</th>
                          <th>Parameter</th>
                          <th>Readings</th>
                          <th>Average</th>
                          <th>Percentile</th>
                          <th>Review</th>
                        </tr>
                      </thead>
                      <tbody>
                        {extraction.table.map((row, index) => (
                          <tr key={`${row.fetus}:${row.parameter}:${index}`}>
                            <td>{row.structure}</td>
                            <td>{row.fetus || 'Unassigned'}</td>
                            <td>{row.parameter}</td>
                            <td>{row.measurements?.join(', ') || '—'}</td>
                            <td>
                              {row.average === null || row.average === undefined
                                ? '—'
                                : `${row.average} ${row.unit || ''}`}
                            </td>
                            <td>
                              {row.percentile === null || row.percentile === undefined
                                ? row.machine_percentile === null || row.machine_percentile === undefined
                                  ? '—'
                                  : `${row.machine_percentile} (machine)`
                                : `${row.percentile} (${row.reference || 'reference'})`}
                            </td>
                            <td>
                              {row.needs_review ? 'Flagged' : 'Verify'}
                              {row.notes?.length ? ` · ${row.notes.join('; ')}` : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              ) : null}
              {extraction.flags?.length ? (
                <details>
                  <summary>Extraction review flags ({extraction.flags.length})</summary>
                  <ul>
                    {extraction.flags.map((flag, index) => (
                      <li key={index}>{flag}</li>
                    ))}
                  </ul>
                </details>
              ) : null}
              {extraction.report_pages?.length ? (
                <details open className="my-3">
                  <summary>Report details ({extraction.report_pages.length} pages; OCR requires review)</summary>
                  {extraction.report_pages.map((page, index) => (
                    <div
                      key={`${page.sop_instance_uid}:${page.frame_index ?? 0}:${index}`}
                      className="my-3 rounded border p-3">
                      <p>
                        Source image {index + 1}
                        {page.frame_index !== null && page.frame_index !== undefined
                          ? ` · frame ${page.frame_index + 1}`
                          : ''}
                      </p>
                      {page.fields.length ? (
                        <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
                          {page.fields.map((field, fieldIndex) => (
                            <p key={`${field.label}:${fieldIndex}`}>
                              <strong>{field.label}:</strong> {field.value}
                              {field.confidence < 0.6 ? ' · verify OCR' : ''}
                            </p>
                          ))}
                        </div>
                      ) : null}
                      <details>
                        <summary>Read complete page text</summary>
                        <pre className="whitespace-pre-wrap text-xs">{page.text}</pre>
                      </details>
                    </div>
                  ))}
                </details>
              ) : null}
              {extractionReady && (
                <p>
                  Check the summary and OCR report details against the source images, then confirm your review below to
                  include this extraction in the draft impression. The summary does not automatically change the
                  pregnancy flowsheet.
                </p>
              )}
            </div>
            {!!study.context?.calculations.length && (
              <>
                <h3>Standardized analysis</h3>
                <p>Profile revision {study.context?.profile?.revision || 0}</p>
                {study.context?.calculations.map((c) => (
                  <p key={`${c.metric}:${c.fetus}`}>
                    Fetus {c.fetus} · {c.metric}: {c.value === null ? c.status : `${c.value.toFixed(1)} ${c.unit}`} ·{' '}
                    {c.formula}
                  </p>
                ))}
              </>
            )}
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="imaging-comments">Sonographer comments</FieldLabel>
                <Textarea
                  id="imaging-comments"
                  rows={5}
                  value={comments}
                  disabled={!editable || busy}
                  onChange={(e) => {
                    setComments(e.target.value);
                    setEvidenceDirty(true);
                  }}
                />
              </Field>
            </FieldGroup>
            {transcriptReview && (
              <Alert>
                <AlertDescription>
                  Review the transcript against the recording before saving evidence.
                  <Button
                    variant="outline"
                    onClick={() => {
                      setTranscriptReview(false);
                      setAudio(null);
                    }}>
                    Transcript reviewed
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            {dictating && (
              <p role="status">
                Transcribing with the AI scribe service… Keep this study open. You can download the recording while it
                processes.
              </p>
            )}
            {audio && (
              <div className="flex flex-wrap gap-2 my-3">
                <Button disabled={busy || !transcription || transcriptReview} onClick={() => void run(transcribe)}>
                  Transcribe recording
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    const url = URL.createObjectURL(audio);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'sonographer-comments';
                    a.click();
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                  }}>
                  Download recording
                </Button>
                <Button
                  disabled={busy}
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm('Discard this recording?')) setAudio(null);
                  }}>
                  Discard
                </Button>
              </div>
            )}
            <p>
              The saved Biometry Summary and report details inform the draft impression. Study dating is kept separate
              from the linked pregnancy when dates conflict.
            </p>
          </div>
        </section>
        <section className="flex h-[780px] min-w-0 flex-col rounded-lg border">
          <header className="border-b p-4">
            <h2>Report setup</h2>
            <p className="text-sm">Choose a template, review the evidence, then create the prelim below.</p>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 flex flex-col gap-4">
            <FieldGroup>
              <Field>
                <FieldLabel>Acquisition date</FieldLabel>
                <Input
                  type="date"
                  value={acquired}
                  disabled={!editable || busy}
                  aria-label="Acquisition date"
                  onChange={(e) => {
                    setAcquired(e.target.value);
                    setEvidenceDirty(true);
                  }}
                />
              </Field>
            </FieldGroup>

            {(study.context?.dating?.fetus_count || 1) > 1 && (
              <Field>
                <FieldLabel htmlFor="report-fetus">Fetus</FieldLabel>
                <select
                  id="report-fetus"
                  className="rounded border bg-background p-2"
                  value={fetus}
                  disabled={!editable || busy}
                  onChange={(e) => {
                    if (
                      draftDirty &&
                      !window.confirm('Changing fetus will replace the current unsaved report. Continue?')
                    )
                      return;
                    setFetus(e.target.value);
                    setReportPayload(null);
                    setReportForm(null);
                    setImpression('');
                    setRecommendations('');
                    setDraftDirty(true);
                  }}>
                  <option value="">Select fetus</option>
                  {Array.from({ length: study.context?.dating?.fetus_count || 1 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1)}>
                      Fetus {i + 1}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <div className="grid grid-cols-[3fr_1fr] items-end gap-2">
              <Field>
                <FieldLabel>Template</FieldLabel>
                <Select
                  value={template}
                  disabled={!editable || busy}
                  onValueChange={(v) => {
                    if (
                      draftDirty &&
                      !window.confirm('Changing template replaces the unsaved report fields. Continue?')
                    )
                      return;
                    setReportPayload(null);
                    setReportForm(null);
                    setImpression('');
                    setRecommendations('');
                    setTemplate(v);
                    setDraftDirty(true);
                  }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {templates.data?.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
              <Button
                variant="outline"
                disabled={!editable || busy || !!audio || !transcription}
                onClick={() => void run(record)}>
                {recording ? <Square data-icon="inline-start" /> : <Mic data-icon="inline-start" />}
                {recording ? 'Stop' : 'Record'}
              </Button>
            </div>
            {!transcription && <p>Dictation service is not configured. Typed comments are available.</p>}
            {needsEvidenceSave && editable && (
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={evidenceConfirmed}
                  disabled={busy || extracting || recording || transcriptReview || !!audio}
                  onCheckedChange={(value) => setEvidenceConfirmed(value === true)}
                />
                I reviewed the current Biometry Summary, report details, acquisition date, and comments against the
                source images.
              </label>
            )}
            <div role="status" aria-live="polite" className="text-sm">
              {draftBlockers.length ? (
                <ul className="list-disc pl-5">
                  {draftBlockers.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              ) : (
                <p>
                  {needsEvidenceSave
                    ? 'Ready: your reviewed evidence will be saved before generating the impression.'
                    : 'Ready to generate from the saved reviewed evidence.'}
                </p>
              )}
            </div>
            <Button onClick={() => void run(generate)} disabled={busy || draftBlockers.length > 0}>
              {busy
                ? 'Working…'
                : needsEvidenceSave
                  ? reportForm
                    ? 'Save evidence & create draft report'
                    : 'Save evidence & create draft impression'
                  : reportForm
                    ? 'Create draft report'
                    : 'Create Draft Impression'}
            </Button>
            {needsEvidenceSave && editable && (
              <Button
                variant="ghost"
                disabled={
                  busy ||
                  extracting ||
                  recording ||
                  transcriptReview ||
                  !!audio ||
                  !acquired ||
                  !study.pregnancy_id ||
                  !evidenceConfirmed
                }
                onClick={() =>
                  void run(async () => {
                    await saveEvidence();
                    toast.success('Reviewed evidence saved.');
                  })
                }>
                Save evidence only
              </Button>
            )}
            {!locked && (
              <details className="rounded border p-3" open>
                <summary className="cursor-pointer font-medium">Pregnancy trend figures</summary>
                <TrendFigures
                  selected={figures}
                  series={study.context?.series || []}
                  onSelect={
                    editable
                      ? (keys) => {
                          setFigures(keys);
                          setDraftDirty(true);
                        }
                      : undefined
                  }
                />
              </details>
            )}
          </div>
        </section>
        <section ref={reportSection} className="col-span-full min-w-0 rounded-lg border bg-card shadow-sm scroll-mt-20">
          <header className="flex items-center justify-between p-4">
            <h2>Report Draft</h2>
            <Badge variant="outline">
              {locked
                ? 'Signed'
                : draftDirty
                  ? 'Unsaved changes'
                  : study.draft_revision
                    ? 'Saved · unsigned'
                    : 'Not saved'}
            </Badge>
          </header>
          <div aria-label="Report draft" className="mx-auto w-full max-w-6xl p-6 sm:p-10">
            {formLoading && <p role="status">Loading editable report…</p>}
            {formError && <p role="alert">{formError}</p>}
            {generationError && (
              <p role="alert">Draft generation failed: {generationError}. Your existing fields are retained.</p>
            )}
            {reportForm && reportPayload && (
              <EditableUltrasoundReport
                form={reportForm}
                report={reportPayload}
                disabled={!editable || busy}
                onChange={(value) => {
                  setReportPayload(value);
                  setDraftDirty(true);
                }}
              />
            )}
            {!reportForm && !formLoading && !formError && (
              <p className="mb-4">
                Select an ultrasound Jasper template to populate the complete editable report. Other templates provide a
                narrative draft.
              </p>
            )}
            <div className="mt-8" />
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="imaging-impression">Impression</FieldLabel>
                <Textarea
                  id="imaging-impression"
                  rows={10}
                  value={impression}
                  disabled={!editable || busy}
                  onChange={(e) => {
                    setImpression(e.target.value);
                    setDraftDirty(true);
                  }}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="imaging-recommendations">Recommendations</FieldLabel>
                <Textarea
                  id="imaging-recommendations"
                  rows={5}
                  value={recommendations}
                  disabled={!editable || busy}
                  onChange={(e) => {
                    setRecommendations(e.target.value);
                    setDraftDirty(true);
                  }}
                />
              </Field>
            </FieldGroup>
            <h3 className="my-4">Figures at the bottom of this report</h3>
            <TrendFigures
              series={
                (locked ? study.draft.figures : study.context?.series || [])?.filter((s) => figures.includes(s.key)) ||
                []
              }
            />
            {reportForm && reportPayload && <UltrasoundSignatureArea form={reportForm} report={reportPayload} />}
          </div>
          <footer className="flex flex-col gap-3 border-t p-4">
            <p>Save text and selected figures before provider review.</p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => void run(saveDraft)}
                disabled={
                  !editable ||
                  busy ||
                  formLoading ||
                  !!formError ||
                  evidenceDirty ||
                  !savedExtractionCurrent ||
                  extracting ||
                  !template ||
                  !impression.trim() ||
                  recording ||
                  transcriptReview
                }>
                Save draft
              </Button>
              <Button
                variant="outline"
                disabled={
                  !clinical || locked || busy || formLoading || draftDirty || evidenceDirty || !study.draft_revision
                }
                onClick={() => void run(reviewSign)}>
                Review and sign
              </Button>
            </div>
          </footer>
        </section>
      </div>
      <Dialog
        open={!!preview}
        onOpenChange={(v) => {
          if (!v && !busy) setPreview(null);
        }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Review saved report and sign</DialogTitle>
          </DialogHeader>
          {preview && (
            <>
              <p>
                {preview.snapshot.patient_name} · {preview.snapshot.title}
              </p>
              <pre className="whitespace-pre-wrap">{preview.snapshot.content}</pre>
              <TrendFigures series={preview.snapshot.imaging?.figures || []} />
              <p>
                {preview.signer_name}, {preview.signer_credentials}
              </p>
              <img alt="Your signature" src={preview.image_url} className="max-h-24 object-contain" />
              {preview.snapshot.signature_text && (
                <p className="whitespace-pre-wrap">{preview.snapshot.signature_text}</p>
              )}
              <label className="flex items-start gap-2">
                <input type="checkbox" checked={attested} onChange={(e) => setAttested(e.target.checked)} />
                {preview.attestation}
              </label>
            </>
          )}
          <DialogFooter>
            <Button disabled={busy} variant="outline" onClick={() => setPreview(null)}>
              Back to draft
            </Button>
            <Button disabled={busy || !attested} onClick={() => void run(sign)}>
              Sign and lock report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
